/* eslint-disable guard-for-in */
import {
  getCurrencySymbol,
  unixTimeStampToNewspaperTimezoneDate
} from 'lib/helpers';
import { TransactionType, Product } from 'lib/enums';
import { isValidProduct } from 'lib/enums/Product';
import { EPayoutData } from 'lib/types';
import {
  PayoutChildRowData,
  PayoutGroup,
  PayoutParentRowData,
  StripeMetadataPerProductPayoutFields
} from 'lib/types/payout';

import { ALL_PRODUCT_TYPES_STRING } from './payoutFilterTypes';

function payoutMetadataPertainsToAdType(
  productPayoutMetadata: StripeMetadataPerProductPayoutFields | undefined
) {
  if (!productPayoutMetadata) {
    return false;
  }
  const { charges, refunds, transfers, other } = productPayoutMetadata;
  return charges > 0 || refunds > 0 || transfers > 0 || other > 0;
}

/**
 * Applies the payouts table ad-type filter. An empty filter or the "all
 * products" sentinel returns every payout; a specific product returns only
 * payouts whose metadata records activity for that product.
 */
export function filterPayoutsByAdType(payouts: EPayoutData[], adType: string) {
  if (!adType || adType === ALL_PRODUCT_TYPES_STRING) {
    return payouts;
  }
  if (!isValidProduct(adType)) {
    return [];
  }
  return payouts.filter(payout =>
    payoutMetadataPertainsToAdType(payout.metadata?.[adType])
  );
}

/**
 * A payout is considered "enriched" once the balance-transactions endpoint has
 * attached its transaction arrays. Failed lookups come back without them, so we
 * use this to avoid caching an incomplete payout (which would otherwise render
 * an empty expansion until a full page refresh).
 */
export function payoutHasBalanceTransactionData(payout: EPayoutData) {
  return (
    payout.payments !== undefined ||
    payout.charges !== undefined ||
    payout.refunds !== undefined ||
    payout.feeRefunds !== undefined
  );
}

const getAdTypeFromMetadata = (
  metadata: Record<string, string> | undefined
): Product => {
  const adType = metadata?.adType;
  if (!adType || !isValidProduct(adType)) {
    return Product.Notice;
  }
  return adType;
};

/**
 * Builds the parent row and expandable child rows for a single payout from its
 * (already fetched) balance transactions.
 */
export function buildPayoutGroupRows(
  payout: EPayoutData,
  ianaTimezone: string | undefined
): PayoutGroup {
  const currencySymbol = getCurrencySymbol(payout.currency);
  const parentRow: PayoutParentRowData = {
    arrival_date: unixTimeStampToNewspaperTimezoneDate(
      payout.arrival_date,
      ianaTimezone
    ),
    created: unixTimeStampToNewspaperTimezoneDate(payout.created, ianaTimezone),
    amount: `${currencySymbol}${(payout.amount / 100).toFixed(2)}`,
    id: payout.id,
    type: 'PAYOUT',
    status: payout.status
  };

  const childRow: PayoutChildRowData[] = [];
  if (payout?.payments?.length) {
    for (const payment of payout.payments) {
      if (!payment?.source?.source_transfer) {
        continue;
      }

      const type =
        payment.source.source_transfer.metadata.type === 'revenueShare'
          ? TransactionType.dividend.label
          : TransactionType.payment.label;

      const adType = getAdTypeFromMetadata(
        payment.source.source_transfer.metadata
      );

      childRow.push({
        id: payment.id,
        type,
        adType,
        amount: `${currencySymbol}${(
          (payment.source.amount || 0) / 100
        ).toFixed(2)}`,
        parentId: payout.id,
        noticeId:
          type !== TransactionType.dividend.label
            ? payment.source.source_transfer.metadata.userNoticeId || ''
            : '',
        orderId: payment.source.source_transfer.metadata.userNoticeId ?? '',
        newspaperOrderId:
          payment.source.source_transfer.metadata.newspaperOrderId ?? '',
        receipt: payment.source.receipt_url
      });
    }
  }
  if (payout?.charges?.length) {
    for (const charge of payout.charges) {
      if (!charge.source) {
        continue;
      }

      const adType = getAdTypeFromMetadata(
        charge.source.source_transfer?.metadata
      );

      childRow.push({
        id: charge.id,
        type: TransactionType.processing_fee_deduction.label,
        adType,
        amount: `-${currencySymbol}${(
          (charge.source.amount || 0) / 100
        ).toFixed(2)}`,
        parentId: payout.id,
        noticeId: charge.source.metadata.userNoticeId ?? '',
        receipt: charge.source.receipt_url
      });
    }
  }
  if (payout?.refunds?.length) {
    for (const refund of payout.refunds) {
      if (!refund.source?.transfer_reversal) {
        continue;
      }

      const adType = getAdTypeFromMetadata(
        refund.source.source_transfer?.metadata
      );

      const transferReversalType =
        refund.source.transfer_reversal?.metadata.type;
      const sourceType = refund.source.metadata.type;
      const type =
        TransactionType.by_key(transferReversalType)?.label ||
        TransactionType.by_key(sourceType)?.label ||
        '';
      childRow.push({
        id: refund.id,
        type,
        adType,
        amount: `-${currencySymbol}${(
          (refund.source.amount || 0) / 100
        ).toFixed(2)}`,
        parentId: payout.id,
        noticeId: refund.source.transfer_reversal?.metadata?.userNoticeId || '',
        receipt: refund.source.receipt_url
      });
    }
  }
  if (payout?.feeRefunds?.length) {
    for (const feeRefund of payout.feeRefunds) {
      if (!feeRefund.source) {
        continue;
      }

      const adTypeFromMetadata =
        feeRefund.source.source_transfer?.metadata.adType;
      const adType = isValidProduct(adTypeFromMetadata)
        ? adTypeFromMetadata
        : Product.Notice;

      const type = TransactionType.feeRefund.label;
      childRow.push({
        id: feeRefund.id,
        type,
        adType,
        amount: `${currencySymbol}${(
          (feeRefund.source.amount || 0) / 100
        ).toFixed(2)}`,
        parentId: payout.id,
        noticeId: feeRefund.source.metadata?.userNoticeId || '',
        transactionTypeValue: TransactionType.feeRefund.key
      });
    }
  }
  /** This block handles to display manual payouts, this can be of two types:
   * 1. A payout reversed from Stripe dashboard
   * 2. A payout manually paid from Stripe dashboard
   */
  if (!payout.automatic) {
    if (payout.original_payout) {
      childRow.push({
        id: payout.id,
        adType: null,
        type: TransactionType.paymentReversed.label,
        amount: `${currencySymbol}${(payout.amount / 100).toFixed(2)}`,
        parentId: payout.id,
        noticeId: '' // reversed payouts do not have noticeId in Stripe metadata
      });
    } else {
      childRow.push({
        id: payout.id,
        type: TransactionType.manualPayment.label,
        adType: null,
        amount: `${currencySymbol}${(payout.amount / 100).toFixed(2)}`,
        parentId: payout.id,
        noticeId: '' // manual payouts do not have noticeId in Stripe metadata
      });
    }
  }
  return {
    parent: parentRow,
    children: childRow
  };
}
